import { Link, usePage } from '@inertiajs/react';
import { ActionIcon, Badge, Box, Button, Indicator, Menu, Text } from '@mantine/core';
import {
  IconBell,
  IconCategory,
  IconCreditCard,
  IconFileText,
  IconFileUpload,
  IconPlug,
  IconSchool,
  IconSettings,
  IconShield,
} from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import type { SharedProps } from '@/types';

export default function OrganizationSettingsMenu({
  organizationId,
  showSubscription,
}: {
  organizationId: string;
  showSubscription: boolean;
}) {
  const { t } = useTranslation();
  const { isMobile } = useMediaQuery();
  const { pending_document_counts: pendingDocumentCounts } = usePage<SharedProps>().props;
  const pendingCount = pendingDocumentCounts[organizationId] ?? 0;
  const currentPath = window.location.pathname;
  const variant = currentPath.startsWith('/organizations/') ? 'light' : 'subtle';

  const menuItems: ({ divider: string } | { path: string; icon: ReactNode; label: string })[] = [
    { path: 'authorizations', icon: <IconShield size={16} />, label: t('menu.authorizations') },
    { path: 'instructions', icon: <IconFileText size={16} />, label: t('menu.instructions') },
    {
      path: 'document_notification_settings',
      icon: <IconSchool size={16} />,
      label: t('document_notification_settings.title'),
    },
    { path: 'reminder_instructions', icon: <IconBell size={16} />, label: t('menu.reminders') },
    { divider: 'integrations' },
    { path: 'integrations', icon: <IconPlug size={16} />, label: t('integrations.title') },
    {
      path: 'document_classification_settings',
      icon: <IconCategory size={16} />,
      label: t('document_classification_settings.title'),
    },
    { path: 'imports', icon: <IconFileUpload size={16} />, label: t('menu.imports') },
    ...(showSubscription
      ? [
          { divider: 'subscription' },
          { path: 'subscription', icon: <IconCreditCard size={16} />, label: t('menu.subscription') },
        ]
      : []),
  ];

  return (
    <Menu position='bottom-start' width={240}>
      <Menu.Target>
        {isMobile ? (
          <ActionIcon
            variant={variant}
            color='gray'
            size='lg'
            aria-label={t('menu.settings')}
            style={{ flexShrink: 0 }}
          >
            <Indicator color='blue' disabled={pendingCount === 0} size={8}>
              <IconSettings size={20} />
            </Indicator>
          </ActionIcon>
        ) : (
          <Button
            variant={variant}
            color='gray'
            leftSection={<IconSettings size={20} />}
            rightSection={pendingCount > 0 ? <Box w={8} h={8} bg='blue.6' bdrs='50%' /> : undefined}
          >
            <Text component='span' inherit truncate maw={140}>
              {t('menu.settings')}
            </Text>
          </Button>
        )}
      </Menu.Target>
      <Menu.Dropdown>
        {menuItems.map((item) =>
          'divider' in item ? (
            <Menu.Divider key={`divider-${item.divider}`} />
          ) : (
            <Menu.Item
              key={item.path}
              component={Link}
              href={`/organizations/${organizationId}/${item.path}`}
              leftSection={item.icon}
              rightSection={
                item.path === 'imports' && pendingCount > 0 ? (
                  <Badge color='blue' size='sm'>
                    {pendingCount}
                  </Badge>
                ) : undefined
              }
              // The dropdown replaces the sidebar, so the current section is marked here instead.
              bg={currentPath.includes(`/${item.path}`) ? 'var(--mantine-color-gray-1)' : undefined}
            >
              {item.label}
            </Menu.Item>
          ),
        )}
      </Menu.Dropdown>
    </Menu>
  );
}
